import React, { useEffect, useState } from "react";

/**
 * Copies text without assuming a secure context.
 *
 * navigator.clipboard exists only in a secure context. The dev host is
 * http://merchant-management.localtest.me:40003 -- plain http, and browsers trust
 * "localhost" and "*.localhost" by name, not any domain that happens to resolve to
 * 127.0.0.1. So the modern API is undefined in exactly the environment this gets used in
 * during development, and the deprecated execCommand path is the one that actually runs
 * there. Production is https, where the modern path is preferred.
 *
 * jsdom implements neither, so this returns false under test unless document.execCommand
 * is stubbed.
 */
export async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      //  Blocked, or the document was not focused. Fall through to the textarea.
    }
  }

  //  Off-screen rather than display:none or [hidden]: an element that is not rendered
  //  cannot hold a selection, so the copy would silently do nothing. position:fixed keeps
  //  the page from scrolling to it.
  const previouslyFocused = document.activeElement;
  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.top = "-1000px";
  field.style.opacity = "0";
  document.body.appendChild(field);

  let copied = false;
  try {
    field.select();
    //  select() on its own is ignored by iOS Safari.
    field.setSelectionRange(0, text.length);
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    field.remove();
    //  Selecting the textarea took focus. Without putting it back, a keyboard user loses
    //  their place in the table every time they copy an id.
    previouslyFocused?.focus?.();
  }

  return copied;
}

/**
 * An identifier with a copy button beside it.
 *
 * These are 36-character GUIDs that people copy into support tickets and API calls far
 * more often than they read, and selecting one by hand out of a table row is fiddly.
 */
export function CopyableId({ value, label }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  //  A blank cell stays blank. An invite has no merchant id until onboarding finishes.
  if (!value) return null;

  const handleCopy = async () => {
    //  Only confirms if the copy actually happened. Claiming success on a failed copy is
    //  worse than no button at all: the user walks away and pastes something stale.
    if (await copyToClipboard(value)) setCopied(true);
  };

  return (
    <span className="d-inline-flex align-items-center gap-1">
      <span>{value}</span>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 border-0 align-baseline text-secondary"
        aria-label={`Copy ${label}`}
        onClick={handleCopy}
      >
        <i
          className={`bi ${copied ? "bi-clipboard-check" : "bi-clipboard"}`}
          aria-hidden="true"
        />
      </button>
    </span>
  );
}

export default CopyableId;
